import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import type { AuthResponse } from '@/apis/auth/auth.type';
import {
  setAdminAuthCookies,
  clearAdminAuthCookies,
  getAdminAuthData,
} from '@/lib/cookies';

export interface AuthState {
  accessToken?: string;
  redirectUrl: string;
  user?: AuthResponse;

  setUser: (user: AuthResponse) => void;
  setRedirectUrl: (redirectUrl: string) => void;
  setAccessToken: (accessToken: string) => void;
  clearUser: () => void;
  init: () => void;
  isAuthenticated: () => boolean;
}

const initialState = {
  redirectUrl: '/admin',
  user: undefined,
  accessToken: undefined,
};

const name = 'authStore';

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) =>
        ({
          ...initialState,

          setUser: (user) => {
            const authToken =
              user.auth_key || user.auth_token || user.token || '';

            let hasValidExpiration = false;
            let expirationDate: Date;

            if (user.expired_at?.Valid && user.expired_at?.Time) {
              hasValidExpiration = true;
              expirationDate = new Date(user.expired_at.Time);
            } else {
              console.log('No valid expiration date found in response.');
              if (typeof window !== 'undefined') {
                console.warn('No expiration date found. Logging out user.');
                setTimeout(() => {
                  get().clearUser();
                }, 0);
                return;
              }
              expirationDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
            }

            set(() => ({
              user,
              accessToken: authToken,
            }));

            setAdminAuthCookies(authToken, user, expirationDate);

            if (typeof window !== 'undefined' && authToken) {
              localStorage.setItem('adminToken', authToken);
              console.log(
                'Admin token stored in localStorage for compatibility'
              );
            }
          },

          clearUser: () => {
            set(() => ({
              user: undefined,
              accessToken: undefined,
            }));

            clearAdminAuthCookies();

            if (typeof window !== 'undefined') {
              localStorage.removeItem('adminToken');
            }
          },

          setRedirectUrl: (redirectUrl) => {
            set(() => ({
              redirectUrl,
            }));
          },

          setAccessToken: (accessToken) => {
            set(() => ({
              accessToken,
            }));
          },

          init: () => {
            set(() => ({
              ...initialState,
            }));
          },

          isAuthenticated: () => {
            const { token } = getAdminAuthData();
            if (get().accessToken || token) return true;

            if (typeof window !== 'undefined') {
              const adminToken = localStorage.getItem('adminToken');
              return !!adminToken;
            }

            return false;
          },
        } as AuthState),
      { name, storage: createJSONStorage(() => localStorage) }
    ),
    { name }
  )
);
